export type SchedulerBootstrapMilestone =
  | 'scheduler-mounted'
  | 'permission-loading-started'
  | 'permission-ready'
  | 'users-ready'
  | 'shifts-refresh-started'
  | 'shifts-refresh-completed'
  | 'render-gate-ready'
  | 'first-paint-complete'
  | 'display-settings-ready'
  | 'week-settings-ready'
  | 'daily-context-ready';

export type SchedulerRenderContributor = 'Dashboard' | 'BeosztasApp' | 'SchedulerGrid' | 'SchedulerGridSkeleton';
export type SchedulerDerivedContributor = 'filteredUsers' | 'orderedUsers' | 'totals';
export type SchedulerListenerName = 'positions' | 'display-settings' | 'export-settings' | 'week-settings';
export type SchedulerListenerEvent = 'subscribe' | 'unsubscribe';
export type SchedulerAbortClassification = 'expected' | 'unknown' | 'unexpected';
export type SchedulerRefreshDiagnosticType = 'start' | 'complete' | 'skipped' | 'deduped' | 'failed' | 'timeout' | 'cancelled' | 'superseded';

export type SchedulerAbortDiagnosticInput = {
  operation: string;
  module: string;
  reason: string;
  classification: SchedulerAbortClassification;
  replacementStarted?: boolean;
};

export type SchedulerRefreshDiagnosticInput = {
  type: SchedulerRefreshDiagnosticType;
  reason: string;
  mode: 'hard' | 'soft';
  durationMs?: number;
  detail?: string;
};

type SchedulerBootstrapProfilerOptions = {
  enabled: boolean;
  now?: () => number;
  log?: (summary: string) => void;
};

type CountStats = { count: number; totalDurationMs: number };
type ListenerStats = { subscribes: number; unsubscribes: number; active: boolean; reasons: string[] };
type AbortStats = SchedulerAbortDiagnosticInput & { classification: SchedulerAbortClassification; atMs: number };
type RefreshStats = SchedulerRefreshDiagnosticInput & { atMs: number };

const formatMs = (value: number): string => `${Math.max(0, Math.round(value))} ms`;

const milestoneLabels: Record<SchedulerBootstrapMilestone, string> = {
  'scheduler-mounted': 'scheduler mounted',
  'permission-loading-started': 'permission loading started',
  'permission-ready': 'permission ready',
  'users-ready': 'users ready',
  'shifts-refresh-started': 'shift refresh started',
  'shifts-refresh-completed': 'shifts ready',
  'display-settings-ready': 'display settings ready',
  'week-settings-ready': 'week settings ready',
  'daily-context-ready': 'daily context ready',
  'render-gate-ready': 'render gate ready',
  'first-paint-complete': 'first paint',
};

const blockingMilestones: Array<{ milestone: SchedulerBootstrapMilestone; label: string }> = [
  { milestone: 'permission-ready', label: 'permission' },
  { milestone: 'users-ready', label: 'users directory' },
  { milestone: 'shifts-refresh-completed', label: 'shifts refresh' },
  { milestone: 'render-gate-ready', label: 'render gate / row derivation' },
];

export const SCHEDULER_DIAGNOSTICS_QUERY_PARAM = 'schedulerDiagnostics';
export const SCHEDULER_DIAGNOSTICS_STORAGE_KEY = 'mintleaf.schedulerDiagnostics';

type SchedulerDiagnosticsEnabledInput = {
  isDev?: boolean;
  search?: string | null;
  getLocalStorageValue?: (key: string) => string | null;
};

export const resolveSchedulerDiagnosticsEnabled = ({
  isDev = false,
  search = '',
  getLocalStorageValue,
}: SchedulerDiagnosticsEnabledInput): boolean => {
  if (isDev) return true;

  try {
    const normalizedSearch = search?.startsWith('?') ? search.slice(1) : search || '';
    const params = new URLSearchParams(normalizedSearch);
    if (params.get(SCHEDULER_DIAGNOSTICS_QUERY_PARAM) === '1') return true;
  } catch {
    // Ignore malformed search strings; diagnostics must never break bootstrap.
  }

  try {
    return getLocalStorageValue?.(SCHEDULER_DIAGNOSTICS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const isSchedulerDiagnosticsEnabled = (): boolean => {
  const isDev = Boolean(import.meta.env?.DEV);
  if (typeof window === 'undefined') {
    return resolveSchedulerDiagnosticsEnabled({ isDev });
  }

  return resolveSchedulerDiagnosticsEnabled({
    isDev,
    search: window.location?.search || '',
    getLocalStorageValue: key => window.localStorage.getItem(key),
  });
};

export const createSchedulerBootstrapProfiler = ({
  enabled,
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  log = summary => console.debug(summary),
}: SchedulerBootstrapProfilerOptions) => {
  let originAt = now();
  const milestones = new Map<SchedulerBootstrapMilestone, number>();
  const renders = new Map<SchedulerRenderContributor, number>();
  const derived = new Map<SchedulerDerivedContributor, CountStats>();
  const listeners = new Map<SchedulerListenerName, ListenerStats>();
  const aborts: AbortStats[] = [];
  const refreshes: RefreshStats[] = [];
  let completed = false;

  const elapsed = (): number => now() - originAt;

  const markMilestone = (milestone: SchedulerBootstrapMilestone): void => {
    if (!enabled || milestones.has(milestone)) return;
    const timestamp = now();
    if (milestone === 'scheduler-mounted' && milestones.size === 0) {
      originAt = timestamp;
      milestones.set(milestone, 0);
      return;
    }
    milestones.set(milestone, timestamp - originAt);
  };

  const recordRender = (contributor: SchedulerRenderContributor): void => {
    if (!enabled || completed) return;
    renders.set(contributor, (renders.get(contributor) ?? 0) + 1);
  };

  const recordDerived = (contributor: SchedulerDerivedContributor, durationMs: number): void => {
    if (!enabled || completed) return;
    const current = derived.get(contributor) ?? { count: 0, totalDurationMs: 0 };
    derived.set(contributor, {
      count: current.count + 1,
      totalDurationMs: current.totalDurationMs + Math.max(0, durationMs),
    });
  };

  const recordListener = (name: SchedulerListenerName, event: SchedulerListenerEvent, reason: string): void => {
    if (!enabled || completed) return;
    const current = listeners.get(name) ?? { subscribes: 0, unsubscribes: 0, active: false, reasons: [] };
    if (event === 'subscribe') {
      current.subscribes += 1;
      current.active = true;
    } else {
      current.unsubscribes += 1;
      current.active = false;
    }
    current.reasons.push(`${event}:${reason}`);
    listeners.set(name, current);
  };

  const recordAbort = (input: SchedulerAbortDiagnosticInput): void => {
    if (!enabled || completed) return;
    aborts.push({
      ...input,
      atMs: elapsed(),
    });
  };

  const recordRefresh = (input: SchedulerRefreshDiagnosticInput): void => {
    if (!enabled || completed) return;
    refreshes.push({ ...input, atMs: elapsed() });
  };

  const buildTimelineLines = (): string[] => [...milestones.entries()]
    .sort(([, left], [, right]) => left - right)
    .map(([milestone, atMs]) => `${formatMs(atMs).padStart(6, ' ')} ${milestoneLabels[milestone]}`);

  const resolveLastBlocker = (): string => {
    const completedBlocking = blockingMilestones
      .map(item => ({ ...item, atMs: milestones.get(item.milestone) }))
      .filter((item): item is { milestone: SchedulerBootstrapMilestone; label: string; atMs: number } => typeof item.atMs === 'number')
      .sort((left, right) => right.atMs - left.atMs);
    return completedBlocking[0]?.label ?? 'unknown';
  };

  const buildListenerLines = (): string[] => {
    if (listeners.size === 0) return ['  none recorded'];
    return [...listeners.entries()].map(([name, stats]) => {
      const restartCount = Math.max(0, stats.subscribes - 1);
      const restartWarning = restartCount > 1 ? ' ⚠ restarted more than once during bootstrap' : '';
      return `  ${name}: subscribe ×${stats.subscribes}, unsubscribe ×${stats.unsubscribes}, restarts ×${restartCount}${restartWarning}`;
    });
  };

  const buildAbortLines = (): string[] => {
    if (aborts.length === 0) return ['  none recorded'];
    const expectedCount = aborts.filter(abort => abort.classification === 'expected').length;
    const unknownCount = aborts.filter(abort => abort.classification === 'unknown').length;
    const unexpectedCount = aborts.filter(abort => abort.classification === 'unexpected').length;
    return [
      `  total: ${aborts.length}`,
      `  expected: ${expectedCount}`,
      `  unknown: ${unknownCount}`,
      `  unexpected: ${unexpectedCount}`,
      ...aborts.slice(0, 8).map(abort => (
        `  ${formatMs(abort.atMs)} ${abort.classification} ${abort.module}.${abort.operation} reason=${abort.reason} replacementStarted=${abort.replacementStarted === true ? 'yes' : 'no'}`
      )),
      ...(aborts.length > 8 ? [`  ...${aborts.length - 8} more`] : []),
    ];
  };

  const buildRefreshLines = (): string[] => {
    if (refreshes.length === 0) return ['  none recorded'];
    return refreshes.map(refresh => (
      `  ${formatMs(refresh.atMs)} ${refresh.reason} ${refresh.mode} ${refresh.type}${refresh.detail ? ` ${refresh.detail}` : ''}${typeof refresh.durationMs === 'number' ? ` ${formatMs(refresh.durationMs)}` : ''}`
    ));
  };

  const buildSummary = (): string => {
    const firstPaint = milestones.get('first-paint-complete') ?? elapsed();
    const renderTotal = [...renders.values()].reduce((total, count) => total + count, 0);
    const renderLines = [...renders.entries()]
      .map(([name, count]) => `  ${name}: ${count}`)
      .join('\n');
    const derivedLines = [...derived.entries()]
      .map(([name, stats]) => `  ${name}: ${stats.count} run(s), ${formatMs(stats.totalDurationMs)}`)
      .join('\n');
    const highestDerived = [...derived.entries()]
      .sort(([, left], [, right]) => right.totalDurationMs - left.totalDurationMs)[0]?.[0] ?? 'none recorded';

    return [
      '[Scheduler Bootstrap Diagnosis]',
      `First paint ........ ${formatMs(firstPaint)}`,
      `Last blocker ....... ${resolveLastBlocker()}`,
      `Highest recompute .. ${highestDerived}`,
      '',
      'Bootstrap timeline',
      ...buildTimelineLines(),
      '',
      `Render count ........ ${renderTotal}`,
      renderLines ? `Render contributors\n${renderLines}` : 'Render contributors\n  none recorded',
      '',
      'Derived recomputation',
      derivedLines || '  none recorded',
      '',
      'Listener restarts',
      ...buildListenerLines(),
      '',
      'AbortErrors',
      ...buildAbortLines(),
      '',
      'Refresh lifecycle',
      ...buildRefreshLines(),
      '',
      'Recommended next optimization: inspect the last blocker, repeated listener restarts, and unexpected AbortErrors before changing loading behavior.',
    ].join('\n');
  };

  const complete = (): string | null => {
    if (!enabled || completed) return null;
    markMilestone('first-paint-complete');
    completed = true;
    const summary = buildSummary();
    log(summary);
    return summary;
  };

  const isEnabled = (): boolean => enabled && !completed;

  return {
    isEnabled,
    markMilestone,
    recordRender,
    recordDerived,
    recordListener,
    recordAbort,
    recordRefresh,
    complete,
    buildSummary,
  };
};

export const schedulerBootstrapProfiler = createSchedulerBootstrapProfiler({
  enabled: isSchedulerDiagnosticsEnabled(),
});
